/**
 * 360Worlds consumer host detection.
 *
 * When the tours app is served from a 360Worlds domain (e.g.
 * `explore.360worlds.com`, or the legacy `watch.360worlds.com` /
 * `watch.360worlds.co.uk`) it runs in "360Worlds mode": only the consumer
 * library + viewer are exposed and the 360Tours agent dashboard/editor/admin
 * are never rendered. The host list is overridable via VITE_WORLDS_HOSTS
 * (comma-separated) for staging/preview.
 */
const DEFAULT_WORLDS_HOSTS = [
  'explore.360worlds.com',
  'watch.360worlds.com',
  'watch.360worlds.co.uk',
];

export function worldsHosts(): string[] {
  const fromVite = (import.meta as any).env?.VITE_WORLDS_HOSTS as string | undefined;
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  const raw = fromVite || nodeEnv?.VITE_WORLDS_HOSTS;
  const list = raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
  return list.length ? list : DEFAULT_WORLDS_HOSTS;
}

/** True when `hostname` (optional port stripped) is a 360Worlds consumer host. */
export function isWorldsHostname(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.split(':')[0].trim().toLowerCase();
  return worldsHosts().includes(host);
}

/** True when the current hostname is a 360Worlds consumer host. */
export function isWorldsHost(): boolean {
  if (typeof window === 'undefined') return false;
  return isWorldsHostname(window.location.hostname);
}

/**
 * On a 360Worlds consumer host, swap the document favicon + title to the
 * 360Worlds brand so the tab matches the marketing site (360worlds.com)
 * rather than showing the 360Tours Studio icon. Safe to call once on startup;
 * a no-op on every other host.
 */
export function applyWorldsBranding(): void {
  if (typeof document === 'undefined' || !isWorldsHost()) return;

  const setIcon = (rel: string, href: string, type?: string) => {
    let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    if (type) link.type = type;
    link.href = href;
  };

  setIcon('icon', '/360worlds-favicon.svg', 'image/svg+xml');
  setIcon('apple-touch-icon', '/360worlds-favicon.png');
  document.title = '360Worlds — Immersive Educational Worlds';

  const theme = document.querySelector('meta[name="theme-color"]');
  if (theme) theme.setAttribute('content', '#06b6d4');

  const setMeta = (name: string, content: string) => {
    let el = document.querySelector(`meta[property="${name}"]`) || document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  const origin = window.location.origin;
  setMeta('description', 'Explore immersive 360° educational worlds on desktop, mobile, tablet and Meta Quest.');
  setMeta('og:site_name', '360Worlds');
  setMeta('og:title', '360Worlds — Immersive Educational Worlds');
  setMeta('og:description', 'Explore immersive 360° educational worlds on desktop, mobile, tablet and Meta Quest.');
  setMeta('og:image', `${origin}/360worlds-logo.png`);
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', '360Worlds — Immersive Educational Worlds');
  setMeta('twitter:description', 'Explore immersive 360° educational worlds on desktop, mobile, tablet and Meta Quest.');
  setMeta('twitter:image', `${origin}/360worlds-logo.png`);
}
